import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { ensureNativeOAuthListener } from '@/features/auth/googleAuth';
import { isNativeApp } from '@/lib/nativeApp';

const MIN_SPLASH_MS = 1400;
const BOOT_ID = 'native-boot';

function removeHtmlBootSplash() {
  document.getElementById(BOOT_ID)?.remove();
}

function isAppEntryPath(pathname: string) {
  return (
    pathname === '/connexion' ||
    pathname === '/inscription' ||
    pathname.startsWith('/auth/') ||
    pathname.startsWith('/app') ||
    pathname.startsWith('/admin')
  );
}

/**
 * Bootstrap APK : garde le logo (splash natif + HTML) jusqu’à la page de connexion,
 * puis masque proprement sans flash de la landing marketing.
 */
export function NativeBootstrap() {
  const location = useLocation();
  const startedAtRef = useRef(Date.now());
  const hiddenRef = useRef(false);

  useEffect(() => {
    if (!isNativeApp()) {
      removeHtmlBootSplash();
      return;
    }

    ensureNativeOAuthListener();
    void StatusBar.setStyle({ style: Style.Light });
    void StatusBar.setBackgroundColor({ color: '#1a3c5e' });
  }, []);

  useEffect(() => {
    if (!isNativeApp() || hiddenRef.current) return;
    if (!isAppEntryPath(location.pathname)) return;

    let cancelled = false;

    async function hideSplashWhenReady() {
      const elapsed = Date.now() - startedAtRef.current;
      const wait = Math.max(0, MIN_SPLASH_MS - elapsed);
      if (wait > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, wait));
      }
      if (cancelled || hiddenRef.current) return;

      hiddenRef.current = true;
      // Masquer d’abord le splash natif (pastille carrée) pendant que #native-boot
      // (logo normal) reste au-dessus — sinon un flash du 2e logo apparaît.
      try {
        await SplashScreen.hide({ fadeOutDuration: 0 });
      } catch {
        // Déjà masqué
      }
      removeHtmlBootSplash();
    }

    void hideSplashWhenReady();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  return null;
}
